import React, { useCallback, useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, X, Loader2 } from "lucide-react";

/**
 * <IdChipPicker> — search-as-you-type picker that renders selected
 * ids as removable chips ABOVE the search field.
 *
 * Used by the Franchise City Homepage editor to replace raw
 * "one-id-per-line" textareas for both curated campaigns and
 * curated vendors. The stored data shape is unchanged (still a
 * list of string ids) — this is purely a UX layer.
 *
 * Props:
 *   value        Array<string>            currently-selected ids
 *   onChange(v)  (ids: string[]) => void  called on add/remove
 *   fetchFn      (q: string) => Promise   returns { data: { items: [...] } }
 *                                         each item MUST have `.id`
 *   renderItem   (item) => ReactNode      how each search result renders
 *   renderChip   (item) => ReactNode      how each selected chip renders
 *                                         (falls back to item.id)
 *   hydrateFn    (ids: string[]) => Promise<Item[]>   optional — resolves
 *                                                     ids you already have
 *                                                     to display data
 *   placeholder  string
 *   testIdPrefix string                   data-testid prefix (default "picker")
 *   disabled     bool
 */
export default function IdChipPicker({
  value = [],
  onChange,
  fetchFn,
  renderItem,
  renderChip,
  hydrateFn,
  placeholder = "Search…",
  testIdPrefix = "picker",
  disabled = false,
}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);  // hydrated
  const [searching, setSearching] = useState(false);
  const debRef = useRef(null);
  const boxRef = useRef(null);

  // Hydrate selected ids → items whenever value changes (for chip
  // labels). We keep a small cache so switching between searches
  // doesn't lose chip labels.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!hydrateFn || value.length === 0) {
        setSelectedItems((prev) => prev.filter((it) => value.includes(it.id)));
        return;
      }
      const known = new Set(selectedItems.map((s) => s.id));
      const missing = value.filter((id) => !known.has(id));
      if (missing.length === 0) {
        // Prune any chips no longer selected
        setSelectedItems((prev) => prev.filter((it) => value.includes(it.id)));
        return;
      }
      try {
        const fresh = await hydrateFn(missing);
        if (cancelled) return;
        setSelectedItems((prev) => {
          const map = new Map(prev.map((p) => [p.id, p]));
          for (const it of fresh) map.set(it.id, it);
          return value.map((id) => map.get(id) || { id, __missing: true });
        });
      } catch {
        // If hydration fails, still show the raw id as the chip
        setSelectedItems((prev) => {
          const map = new Map(prev.map((p) => [p.id, p]));
          return value.map((id) => map.get(id) || { id, __missing: true });
        });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.join("|")]);

  // Debounced search — 250ms.
  useEffect(() => {
    if (!open) return;
    if (debRef.current) clearTimeout(debRef.current);
    debRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await fetchFn(q);
        setResults(r.data?.items || []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 250);
    return () => { if (debRef.current) clearTimeout(debRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, open]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const add = useCallback((item) => {
    if (disabled) return;
    if (value.includes(item.id)) return;
    onChange([...value, item.id]);
    setQ("");
    setOpen(false);
  }, [disabled, value, onChange]);

  const remove = useCallback((id) => {
    if (disabled) return;
    onChange(value.filter((v) => v !== id));
  }, [disabled, value, onChange]);

  return (
    <div className="space-y-2" ref={boxRef} data-testid={`${testIdPrefix}-container`}>
      {selectedItems.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid={`${testIdPrefix}-chips`}>
          {selectedItems.map((it) => (
            <Badge
              key={it.id}
              variant="secondary"
              className="flex items-center gap-2 pl-2 pr-1 py-1 text-xs max-w-xs"
              data-testid={`${testIdPrefix}-chip-${it.id}`}
            >
              <span className="truncate">
                {renderChip ? renderChip(it) : (it.name || it.business_name || it.id)}
              </span>
              <button
                type="button"
                onClick={() => remove(it.id)}
                disabled={disabled}
                className="rounded-full hover:bg-black/10 p-0.5"
                aria-label={`Remove ${it.name || it.business_name || it.id}`}
                data-testid={`${testIdPrefix}-chip-remove-${it.id}`}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      <div className="relative">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="pl-9"
          disabled={disabled}
          data-testid={`${testIdPrefix}-search`}
        />
      </div>

      {open && (
        <div
          className="rounded-lg border-2 border-border bg-background shadow-lg max-h-72 overflow-y-auto"
          data-testid={`${testIdPrefix}-results`}
        >
          {searching ? (
            <div className="flex items-center justify-center gap-2 py-4 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Searching…
            </div>
          ) : results.length === 0 ? (
            <div className="py-4 text-center text-sm text-muted-foreground">
              {q ? "No matches." : "Type to search."}
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {results.map((item) => {
                const already = value.includes(item.id);
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => add(item)}
                      disabled={already || disabled}
                      className={`w-full text-left px-3 py-2 text-sm ${already ? "opacity-50 cursor-default" : "hover:bg-secondary/60"}`}
                      data-testid={`${testIdPrefix}-result-${item.id}`}
                    >
                      {renderItem ? renderItem(item, { already }) : (
                        <div className="flex items-center justify-between gap-2">
                          <span className="truncate">{item.name || item.business_name || item.id}</span>
                          {already && <span className="text-[10px] text-muted-foreground uppercase">Added</span>}
                        </div>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
